import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Home, User, Zap, Cpu, Radio, Trophy, Share2, Layers, Globe, MapPin } from 'lucide-react';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Hackathons from './components/Hackathons';
import Skills from './components/Skills';
import Connect from './components/Connect';
import PortfolioVersions from './components/PortfolioVersions';
import Contact from './components/Contact';
import AdminAnalytics from './components/admin/AdminAnalytics';
import VisitorLocationModal from './components/VisitorLocationModal';
import LocationPromptModal from './components/LocationPromptModal';
import { useAudio, AUDIO_STATES } from './components/AudioManager';
import AnalyticsTracker from './components/AnalyticsTracker';
import useAnalytics from './hooks/useAnalytics';
import SmoothScroll from './components/reactbits/SmoothScroll';
import Magnet from './components/reactbits/Magnet';
import './components/MobileStyles.css';

// Lazy load background effects for better initial performance
const TerminalOverlay = lazy(() => import('./components/TerminalOverlay'));
const TerminalBackground = lazy(() => import('./components/TerminalBackground'));

// Detect device capabilities
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const TERMINAL_BOOT_KEY = 'terminal_boot_seen';

const hasSeenTerminalBoot = () => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(TERMINAL_BOOT_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

// --- Portfolio component (stable reference, defined outside App) ---
const Portfolio = () => {
  const { trackEvent } = useAnalytics();
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [leftMenuExpanded, setLeftMenuExpanded] = useState(false);
  const [showLeftDock, setShowLeftDock] = useState(false);
  const [dockMinimized, setDockMinimized] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const dockRef = useRef(null);
  const leftDockRef = useRef(null);
  
  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuExpanded && dockRef.current && !dockRef.current.contains(event.target)) {
        setMenuExpanded(false);
      }
      if (leftMenuExpanded && leftDockRef.current && !leftDockRef.current.contains(event.target)) {
        setLeftMenuExpanded(false);
      }
    };
    
    if (menuExpanded || leftMenuExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuExpanded, leftMenuExpanded]);

  // Smart dock behavior on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Left dock only appears after scrolling past hero section (~400px)
      if (currentScrollY > 400) {
        setShowLeftDock(true);
      } else {
        setShowLeftDock(false);
        setLeftMenuExpanded(false);
      }

      if (currentScrollY > 200) {
        if (currentScrollY > lastScrollY && !dockMinimized) {
          setDockMinimized(true);
        }
      } else {
        if (dockMinimized) {
          setDockMinimized(false);
        }
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, dockMinimized]);

  // Handle Right AI orb click
  const handleOrbClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuExpanded(prev => !prev);
    setLeftMenuExpanded(false);
  };

  // Handle Left AI orb click
  const handleLeftOrbClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLeftMenuExpanded(prev => !prev);
    setMenuExpanded(false);
  };
  
  // Smooth scroll with menu close
  const handleNavClick = (e, targetId, label) => {
    e.preventDefault();
    setMenuExpanded(false);
    setLeftMenuExpanded(false);
    trackEvent('link_click', { label: `Nav: ${label}`, target: targetId });
    
    if (window.lenis) {
      if (targetId === '#hero' || targetId === '#home') {
        window.lenis.scrollTo(0, { duration: 1.2 });
      } else {
        const target = document.querySelector(targetId);
        if (target) {
          window.lenis.scrollTo(target, { duration: 1.2, offset: 0 });
        }
      }
    } else {
      if (targetId === '#hero' || targetId === '#home') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        const target = document.querySelector(targetId);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }
  };
  
  return (
  <SmoothScroll>
  <div className="portfolio-container">
    {/* Floating Holographic Left Navigation Dock - Only visible after scrolling past hero */}
    <nav className={`holo-dock-left ${showLeftDock ? 'visible' : ''} ${dockMinimized ? 'minimized' : ''}`} ref={leftDockRef}>
      <div className="dock-container">
        {/* Left Orb Button */}
        <button 
          className="ai-orb-logo" 
          onClick={handleLeftOrbClick}
          type="button"
          aria-label="Toggle left quick navigation menu"
        >
          <div className="orb-core" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(0, 255, 209, 0.4), rgba(0, 163, 255, 0.3), rgba(0, 20, 40, 0.9))' }}>
            <div className="neural-ring ring-1"></div>
            <div className="neural-ring ring-2"></div>
            <div className="neural-ring ring-3"></div>
            <div className="ai-center">
              <span className="ai-text">NODES</span>
            </div>
          </div>
          <div className="orb-glow"></div>
        </button>

        {/* Left Orbit Navigation Items */}
        <div className={`nav-orbit-left ${leftMenuExpanded ? 'expanded' : ''}`}>
          <a href="#hackathons" className="orbit-item" data-label="Hackathons" 
            onClick={(e) => handleNavClick(e, '#hackathons', 'Hackathons')}>
            <div className="orbit-icon"><Trophy size={16} color="#00FFD1" /></div>
            <div className="orbit-trail"></div>
          </a>
          <a href="#connect" className="orbit-item" data-label="Connect" 
            onClick={(e) => handleNavClick(e, '#connect', 'Connect')}>
            <div className="orbit-icon"><Share2 size={16} color="#00A3FF" /></div>
            <div className="orbit-trail"></div>
          </a>
          <a href="#versions" className="orbit-item" data-label="Timeline" 
            onClick={(e) => handleNavClick(e, '#versions', 'Timeline')}>
            <div className="orbit-icon"><Layers size={16} color="#FF00FF" /></div>
            <div className="orbit-trail"></div>
          </a>
        </div>
      </div>
    </nav>

    {/* Floating Holographic Right Navigation Dock */}
    <nav className={`holo-dock ${dockMinimized ? 'minimized' : ''}`} ref={dockRef}>
      <div className="dock-container">
        {/* Central Right AI Orb Logo */}
        <button 
          className="ai-orb-logo" 
          onClick={handleOrbClick}
          type="button"
          aria-label="Toggle navigation menu"
        >
          <div className="orb-core">
            <div className="neural-ring ring-1"></div>
            <div className="neural-ring ring-2"></div>
            <div className="neural-ring ring-3"></div>
            <div className="ai-center">
              <span className="ai-text">MENU</span>
            </div>
          </div>
          <div className="orb-glow"></div>
        </button>

        {/* Right Floating Navigation Items with Unique Vibrant Lucide Icons */}
        <div className={`nav-orbit ${menuExpanded ? 'expanded' : ''}`}>
          <a href="#hero" className="orbit-item" data-label="Home" 
            onClick={(e) => handleNavClick(e, '#hero', 'Home')}>
            <div className="orbit-icon"><Home size={18} color="#FFA500" /></div>
            <div className="orbit-trail"></div>
          </a>
          <a href="#about" className="orbit-item" data-label="About"
            onClick={(e) => handleNavClick(e, '#about', 'About')}>
            <div className="orbit-icon"><User size={18} color="#00FFD1" /></div>
            <div className="orbit-trail"></div>
          </a>
          <a href="#projects" className="orbit-item" data-label="Projects"
            onClick={(e) => handleNavClick(e, '#projects', 'Projects')}>
            <div className="orbit-icon"><Zap size={18} color="#FFE600" /></div>
            <div className="orbit-trail"></div>
          </a>
          <a href="#skills" className="orbit-item" data-label="Skills"
            onClick={(e) => handleNavClick(e, '#skills', 'Skills')}>
            <div className="orbit-icon"><Cpu size={18} color="#FF00FF" /></div>
            <div className="orbit-trail"></div>
          </a>
          <a href="#contact" className="orbit-item" data-label="Contact"
            onClick={(e) => handleNavClick(e, '#contact', 'Contact')}>
            <div className="orbit-icon"><Radio size={18} color="#00FF7F" /></div>
            <div className="orbit-trail"></div>
          </a>
        </div>
      </div>
    </nav>

    {/* Main Content */}
    <main className="main-content">
      <Hero />
      <About />
      <Projects />
      <Hackathons />
      <Skills />
      <Contact />
      <Connect />
      <PortfolioVersions />
    </main>

    {/* Footer */}
    <footer className="main-footer">
      <div className="footer-container">
        <div className="footer-grid">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="grid-cell"></div>
          ))}
        </div>
        <div className="footer-content">
          <p>&copy; 2024 Arun Kumar L. All systems operational.</p>
          <div className="footer-status" style={{ gap: '16px', display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => {
                trackEvent('telemetry_click', { category: 'user_action' });
                setIsLocationModalOpen(true);
              }}
              style={{
                background: 'rgba(0, 229, 255, 0.1)',
                border: '1px solid rgba(0, 229, 255, 0.4)',
                color: '#00e5ff',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '0.72rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontFamily: 'monospace'
              }}
              title="View your IP & Location Telemetry"
            >
              <Globe size={12} color="#00e5ff" />
              <span>Telemetry HUD</span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Status: Online</span>
              <div className="status-indicator active"></div>
            </div>
          </div>
        </div>
      </div>
    </footer>

    {/* Visitor IP & Location Telemetry Modal */}
    <VisitorLocationModal
      isOpen={isLocationModalOpen}
      onClose={() => setIsLocationModalOpen(false)}
    />

    {/* Location Permission Prompt Toast */}
    <LocationPromptModal />
  </div>
  </SmoothScroll>
  );
};

// --- RouteShell component (stable reference, defined outside App) ---
const RouteShell = ({ showTerminal, terminalComplete, onTerminalComplete }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin-analytics');

  useEffect(() => {
    if (isAdminRoute) return;
    if (!showTerminal || terminalComplete) return;
    try {
      sessionStorage.setItem(TERMINAL_BOOT_KEY, 'true');
    } catch (error) {
      // Ignore storage errors
    }
  }, [isAdminRoute, showTerminal, terminalComplete]);

  return (
    <>
      {/* Terminal Background - Matrix rain effect (only on desktop) */}
      {!isAdminRoute && !isMobile && !prefersReducedMotion && (
        <Suspense fallback={null}>
          <TerminalBackground opacity={0.05} speed={0.05} />
        </Suspense>
      )}

      {/* Terminal Boot Overlay */}
      {!isAdminRoute && showTerminal && !terminalComplete && (
        <Suspense fallback={null}>
          <TerminalOverlay
            isVisible={showTerminal}
            autoStart={true}
            onComplete={onTerminalComplete}
          />
        </Suspense>
      )}

      {/* Main Portfolio Content */}
      <AnalyticsTracker />
      <Routes>
        <Route path="/" element={<Portfolio />} />
        <Route path="/admin-analytics" element={<AdminAnalytics />} />
      </Routes>
    </>
  );
};

const App = () => {
  const [showTerminal, setShowTerminal] = useState(() => {
    if (prefersReducedMotion) return false;
    return !hasSeenTerminalBoot();
  });
  const [terminalComplete, setTerminalComplete] = useState(() => {
    if (prefersReducedMotion) return true;
    return hasSeenTerminalBoot();
  });
  const { setAudioState } = useAudio();

  useEffect(() => {
    if (terminalComplete || !showTerminal) {
      setAudioState(AUDIO_STATES.HOME);
    }
  }, [terminalComplete, showTerminal, setAudioState]);

  const handleTerminalComplete = () => {
    setTimeout(() => {
      setTerminalComplete(true);
    }, 500);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <RouteShell
          showTerminal={showTerminal}
          terminalComplete={terminalComplete}
          onTerminalComplete={handleTerminalComplete}
        />
      </BrowserRouter>
    </div>
  );
};

export default App;